import { useEffect, useRef, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { billsApi } from "@/lib/billsNative";
import { unitWord } from "@/lib/billsLanguage";

// Inline, debounced picker over the org's local Ingredient Master (no request per keystroke burst).
export const IngredientPicker = ({ lineId, onPick, onCancel, busy }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const n = ++seq.current;
    const t = setTimeout(async () => {
      setLoading(true);
      const r = await billsApi.ingredients(q.trim());
      if (n === seq.current) {
        setItems(r.ok ? r.data.items : []);
        setLoading(false);
      }
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q]);

  const inputId = `ing-search-${lineId}`;
  return (
    <div className="mt-2 space-y-2" data-testid={`ingredient-picker-${lineId}`}>
      <label htmlFor={inputId} className="text-[13px] font-medium text-archival-primary">Choose ingredient</label>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-archival-muted" aria-hidden />
        <input
          id={inputId}
          data-testid={`ingredient-search-${lineId}`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search ingredients"
          autoComplete="off"
          className="w-full h-12 pl-9 pr-3 rounded-md border border-[#E4E0D8] bg-white text-[16px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--action)]"
        />
      </div>
      <ul className="divide-y divide-[#E4E0D8] rounded-md border border-[#E4E0D8] bg-white" aria-label="Ingredient results">
        {loading && items === null && (
          <li className="p-3 text-[13px] text-archival-muted flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</li>
        )}
        {items && items.length === 0 && <li className="p-3 text-[13px] text-archival-muted">No matching ingredient</li>}
        {(items || []).map((it) => (
          <li key={it.ingredient_id}>
            <button
              type="button"
              disabled={busy}
              data-testid={`ingredient-option-${lineId}-${it.ingredient_id}`}
              onClick={() => onPick(it.ingredient_id, it.ingredient_name)}
              className="w-full min-h-[48px] px-3 py-2 text-left flex items-center justify-between gap-3 hover:bg-archival-soft focus-visible:outline-none focus-visible:bg-archival-soft transition-colors"
            >
              <span className="text-[15px] text-archival-primary">{it.ingredient_name}</span>
              <span className="text-[12px] text-archival-muted shrink-0">measured in {unitWord(it.base_uom)}</span>
            </button>
          </li>
        ))}
      </ul>
      {onCancel && (
        <button type="button" onClick={onCancel} data-testid={`ingredient-cancel-${lineId}`}
          className="text-[13px] text-archival-secondary underline underline-offset-2 min-h-[44px]">Cancel</button>
      )}
    </div>
  );
};
